<template>
  <section class="recon-page">
    <top-menu/>
    <section>
      <page-header headerName="Reconciliação de excedentes" :showBtn="false" btnName="" btnRoute="" css="container-fluid"/>

      <div class="page-content container-fluid">

        <p class="intro">
          Boletos de excedente gerados pela rotina. O reajuste de MRR e teto da
          assinatura acontece <strong>aqui</strong>, só <strong>após o pagamento</strong>
          (ou forçado com senha). <em>Sincronizar</em> reconsulta o gateway e não
          mexe em nada; <em>Aplicar</em> sobe teto + MRR.
        </p>

        <!-- KPIs -->
        <div class="kpis">
          <div class="kpi"><span class="kpi-num">{{ kpis.pendentes }}</span><span class="kpi-lbl">Pendentes</span></div>
          <div class="kpi ok"><span class="kpi-num">{{ kpis.pagosNaoAplicados }}</span><span class="kpi-lbl">Pagos a aplicar</span></div>
          <div class="kpi info"><span class="kpi-num">{{ kpis.aplicados }}</span><span class="kpi-lbl">Aplicados</span></div>
          <div class="kpi money"><span class="kpi-num">R$ {{ money(kpis.mrrAReconhecer) }}</span><span class="kpi-lbl">MRR a reconhecer</span></div>
        </div>

        <!-- Filtros -->
        <div class="filters">
          <label>Ano <input type="number" v-model.number="filter.year" min="2024" max="2100" @change="load"/></label>
          <label>Mês
            <select v-model.number="filter.month" @change="load">
              <option :value="null">Todos</option>
              <option v-for="m in 12" :key="m" :value="m">{{ m }}</option>
            </select>
          </label>
          <label>Gateway
            <select v-model.number="filter.gateway" @change="load">
              <option :value="null">Todos</option>
              <option :value="1">IUGU</option>
              <option :value="2">ASAAS</option>
            </select>
          </label>
          <label>Pagamento
            <select v-model.number="filter.status" @change="load">
              <option :value="null">Todos</option>
              <option :value="0">Pendente</option>
              <option :value="1">Pago</option>
              <option :value="3">Vencido</option>
              <option :value="2">Cancelado</option>
            </select>
          </label>
          <button class="btn-secondary" @click="load" :disabled="loading">
            {{ loading ? "Carregando..." : "Atualizar" }}
          </button>
        </div>

        <!-- Importar lote antigo -->
        <div class="import-box">
          <strong>Resgatar um lote antigo</strong> (ex.: setembro, gerado antes desta tela):
          suba a mesma planilha daquele lote; o sistema casa cada linha com o boleto no
          gateway (por vencimento + valor) e cria as linhas aqui, já com o status de pagamento.
          <div class="import-row">
            <input type="file" accept=".csv" @change="onImportFile"/>
            <label>Vencimento do lote <input type="date" v-model="importDueDate"/></label>
            <button class="btn-secondary" :disabled="!canImport || importing" @click="doImport">
              {{ importing ? "Importando..." : "Importar planilha" }}
            </button>
          </div>
        </div>

        <!-- Toolbar de lote -->
        <div class="bulk-bar" v-if="selected.length">
          {{ selected.length }} selecionada(s):
          <button class="btn-secondary" :disabled="working" @click="doSync(selectedRows)">Sincronizar selecionadas</button>
          <button class="btn-primary" :disabled="working" @click="openApply(selectedRows, false)">Aplicar selecionadas</button>
        </div>

        <!-- Tabela -->
        <table class="recon-table" v-if="rows.length">
          <thead>
            <tr>
              <th><input type="checkbox" :checked="allChecked" @change="toggleAll"/></th>
              <th>Empresa</th>
              <th>Gateway</th>
              <th>Venc.</th>
              <th>Valor</th>
              <th>Exced.</th>
              <th>ΔMRR</th>
              <th>Teto (atual → esperado)</th>
              <th>MRR (atual → esperado)</th>
              <th>Pagamento</th>
              <th>Aplicado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id" :class="{ 'row-applied': !!r.appliedAt }">
              <td><input type="checkbox" :checked="isSelected(r)" @change="toggleRow(r)" :disabled="!!r.appliedAt"/></td>
              <td>{{ r.companyName }}<br><span class="shard">{{ r.databaseName }}</span></td>
              <td><span class="badge" :class="gatewayClass(r.gateway)">{{ gatewayLabel(r.gateway) }}</span></td>
              <td>{{ formatDate(r.dueDate) }}</td>
              <td>R$ {{ money(r.boletoValue) }}</td>
              <td>{{ r.excedentes }}</td>
              <td>R$ {{ money(r.mrrDelta) }}</td>
              <td>{{ nz(r.currentMax) }} → {{ expectedMax(r) }}</td>
              <td>R$ {{ money(r.currentMrr) }} → R$ {{ money(expectedMrr(r)) }}</td>
              <td><span class="badge" :class="statusClass(r.paymentStatus)">{{ statusLabel(r.paymentStatus) }}</span></td>
              <td>
                <span v-if="r.appliedAt" class="applied-ok">{{ formatDate(r.appliedAt) }}</span>
                <span v-else class="muted">—</span>
              </td>
              <td class="actions">
                <button class="btn-link" :disabled="working || !!r.appliedAt" @click="doSync([r])">Sincronizar</button>
                <button class="btn-link" :disabled="working || !!r.appliedAt" @click="openApply([r], false)">Aplicar</button>
                <button class="btn-link danger" :disabled="working || !!r.appliedAt" @click="openApply([r], true)">Forçar</button>
                <a v-if="r.boletoUrl" :href="r.boletoUrl" target="_blank">Boleto</a>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="empty">{{ loading ? "Carregando..." : "Nenhuma cobrança de excedente para o filtro atual." }}</p>
      </div>
    </section>

    <!-- Modal de aprovação -->
    <div class="modal-overlay" v-if="modal.open" @click.self="closeModal">
      <div class="modal-card">
        <div class="modal-head">
          <h4>{{ modal.force ? "Forçar aplicação (sem confirmar pagamento)" : "Aplicar excedentes" }}</h4>
          <button class="modal-x" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <p class="modal-count">{{ modal.items.length }} empresa(s) selecionada(s).</p>
          <p class="modal-sum">
            Vai elevar o <strong>teto</strong> em <strong>+{{ modalSum.exced }}</strong> colaborador(es)
            e o <strong>MRR</strong> em <strong>R$ {{ money(modalSum.mrr) }}</strong> no total.
          </p>
          <p v-if="modal.force" class="modal-warn">
            ⚠ Modo forçado: aplica <strong>mesmo sem</strong> pagamento confirmado no gateway.
            Use só quando você tiver certeza de que pagou.
          </p>
          <p v-else class="modal-hint">
            Só serão aplicadas as linhas com pagamento <strong>confirmado</strong> no gateway;
            as não pagas são puladas.
          </p>
          <div class="form-row">
            <label>Senha de operação</label>
            <input type="password" v-model="modal.password" placeholder="senha" autocomplete="off"/>
          </div>

          <div v-if="modal.result" class="modal-result">
            <p>{{ resultSummary(modal.result) }}</p>
            <table class="res-table">
              <tr v-for="(x, i) in modal.result" :key="i" :class="resClass(x.status)">
                <td>{{ x.companyName }}</td><td>{{ x.status }}</td><td>{{ x.message }}</td>
              </tr>
            </table>
          </div>
        </div>
        <div class="modal-foot">
          <button class="btn-secondary" @click="closeModal">Fechar</button>
          <button class="btn-primary" :class="{ danger: modal.force }"
                  :disabled="modal.running || !modal.password" @click="doApply">
            {{ modal.running ? "Aplicando..." : (modal.force ? "Confirmar aplicação forçada" : "Confirmar aplicação") }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  EXCEDENTES_CHARGES,
  EXCEDENTES_CHARGES_SYNC,
  EXCEDENTES_CHARGES_APPLY,
  EXCEDENTES_CHARGES_IMPORT
} from "../../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../../requestConfig";
import { UTIL_SERVICE } from "../../../../../utils";
import topMenu from "../../../components/default/topMenu.vue";
import pageHeader from "../../../components/default/pageHeader.vue";
import swal from "sweetalert";

export default {
  name: "reconciliacaoExcedentes",
  components: { topMenu, pageHeader },
  data() {
    const now = new Date();
    return {
      loading: false,
      working: false,
      rows: [],
      selected: [],
      filter: { year: now.getFullYear(), month: null, gateway: null, status: null },

      importFile: null,
      importDueDate: "",
      importing: false,

      modal: { open: false, force: false, items: [], password: "", running: false, result: null }
    };
  },
  computed: {
    allChecked() {
      const selectable = this.rows.filter((r) => !r.appliedAt);
      return selectable.length > 0 && selectable.every((r) => this.isSelected(r));
    },
    selectedRows() {
      return this.rows.filter((r) => this.isSelected(r));
    },
    canImport() {
      return this.importFile && this.importDueDate;
    },
    kpis() {
      let pendentes = 0, pagosNaoAplicados = 0, aplicados = 0, mrrAReconhecer = 0;
      this.rows.forEach((r) => {
        if (r.appliedAt) { aplicados++; return; }
        if (r.paymentStatus === 1) { pagosNaoAplicados++; mrrAReconhecer += Number(r.mrrDelta || 0); }
        else { pendentes++; }
      });
      return { pendentes, pagosNaoAplicados, aplicados, mrrAReconhecer };
    },
    modalSum() {
      let exced = 0, mrr = 0;
      this.modal.items.forEach((r) => { exced += Number(r.excedentes || 0); mrr += Number(r.mrrDelta || 0); });
      return { exced, mrr };
    }
  },
  beforeMount() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.selected = [];
      const f = this.filter;
      const qs = [];
      if (f.year) qs.push("year=" + f.year);
      if (f.month) qs.push("month=" + f.month);
      if (f.gateway) qs.push("gateway=" + f.gateway);
      if (f.status !== null && f.status !== undefined) qs.push("status=" + f.status);
      const url = EXCEDENTES_CHARGES + (qs.length ? "?" + qs.join("&") : "");
      REQUEST_SERVICE.get(url)
        .then((r) => { this.rows = r.data || []; })
        .catch(() => { this.rows = []; swal("Aviso", "Falha ao carregar as cobranças.", "error"); })
        .then(() => { this.loading = false; });
    },

    // ----- seleção -----
    isSelected(r) { return this.selected.indexOf(r.id) !== -1; },
    toggleRow(r) {
      if (r.appliedAt) return;
      const i = this.selected.indexOf(r.id);
      if (i === -1) this.selected.push(r.id); else this.selected.splice(i, 1);
    },
    toggleAll(e) {
      if (e.target.checked) this.selected = this.rows.filter((r) => !r.appliedAt).map((r) => r.id);
      else this.selected = [];
    },

    // ----- sincronizar (sem senha; não mexe em assinatura) -----
    doSync(items) {
      if (!items || !items.length || this.working) return;
      this.working = true;
      const payload = { charges: items.map((r) => ({ id: r.id, databaseName: r.databaseName })) };
      REQUEST_SERVICE.requestContext("POST", EXCEDENTES_CHARGES_SYNC, null, payload)
        .then((resp) => {
          const res = resp.data || [];
          UTIL_SERVICE.console("SYNC EXCEDENTES:", res);
          swal("Sincronizado", this.resultSummary(res), "success");
          this.load();
        })
        .catch(() => { swal("Aviso", "Falha ao sincronizar.", "error"); })
        .then(() => { this.working = false; });
    },

    // ----- aplicar (modal + senha) -----
    openApply(items, force) {
      if (!items || !items.length) return;
      this.modal = { open: true, force: !!force, items: items.slice(), password: "", running: false, result: null };
    },
    closeModal() { this.modal.open = false; },
    doApply() {
      if (!this.modal.password || this.modal.running) return;
      this.modal.running = true;
      const payload = {
        charges: this.modal.items.map((r) => ({ id: r.id, databaseName: r.databaseName })),
        force: this.modal.force,
        operationPassword: this.modal.password
      };
      REQUEST_SERVICE.requestContext("POST", EXCEDENTES_CHARGES_APPLY, null, payload)
        .then((resp) => {
          this.modal.result = resp.data || [];
          const ok = this.modal.result.filter((x) => x.status === "OK").length;
          swal(ok ? "Aplicado" : "Concluído", this.resultSummary(this.modal.result), ok ? "success" : "warning");
          this.load();
        })
        .catch((e) => {
          const forbidden = e && e.response && e.response.status === 403;
          swal("Aviso", forbidden ? "Senha de operação inválida." : "Falha ao aplicar.", "error");
        })
        .then(() => { this.modal.running = false; });
    },

    // ----- importar lote antigo -----
    onImportFile(e) { this.importFile = e.target.files[0] || null; },
    doImport() {
      if (!this.canImport || this.importing) return;
      this.importing = true;
      this.parseCsv(this.importFile)
        .then((rows) => {
          const payload = { dueDate: this.importDueDate, rows };
          return REQUEST_SERVICE.requestContext("POST", EXCEDENTES_CHARGES_IMPORT, null, payload);
        })
        .then((resp) => {
          const res = resp.data || [];
          UTIL_SERVICE.console("IMPORT EXCEDENTES:", res);
          swal("Importado", this.resultSummary(res), "success");
          this.load();
        })
        .catch(() => { swal("Aviso", "Falha ao importar a planilha. Verifique o formato.", "error"); })
        .then(() => { this.importing = false; });
    },

    // ----- labels / formatação -----
    gatewayLabel(g) { return g === 1 ? "IUGU" : (g === 2 ? "ASAAS" : "—"); },
    gatewayClass(g) { return g === 1 ? "badge-iugu" : "badge-asaas"; },
    statusLabel(s) {
      switch (s) {
        case 1: return "Pago";
        case 2: return "Cancelado";
        case 3: return "Vencido";
        case 5: return "Estornado";
        case 6: return "Pago parcial";
        default: return "Pendente";
      }
    },
    statusClass(s) {
      switch (s) {
        case 1: return "badge-pago";
        case 2: return "badge-cancelado";
        case 3: return "badge-vencido";
        case 6: return "badge-pago";
        default: return "badge-pendente";
      }
    },
    expectedMax(r) { return r.appliedAt ? this.nz(r.currentMax) : (this.nz(r.currentMax) + Number(r.excedentes || 0)); },
    expectedMrr(r) { return r.appliedAt ? Number(r.currentMrr || 0) : (Number(r.currentMrr || 0) + Number(r.mrrDelta || 0)); },
    nz(v) { return v === null || v === undefined ? 0 : v; },
    money(v) {
      const n = Number(v || 0);
      return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) { if (!d) return ""; return new Date(d).toLocaleDateString("pt-BR"); },
    resultSummary(res) {
      const ok = res.filter((x) => x.status === "OK").length;
      const pul = res.filter((x) => x.status === "PULADO").length;
      const fal = res.filter((x) => x.status === "FALHA").length;
      return `${ok} OK, ${pul} pulada(s), ${fal} falha(s).`;
    },
    resClass(s) { return s === "OK" ? "res-ok" : (s === "FALHA" ? "res-err" : "res-skip"); },

    // ----- CSV (mesmo parser posicional de 15 colunas da tela de excedentes) -----
    parseCsv(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const text = evt.target.result;
            const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
            if (lines.length < 2) return resolve([]);
            const delimiter = lines[0].indexOf(";") !== -1 ? ";" : ",";
            const rows = lines.slice(1).map((line, idx) => {
              const cells = this.splitCsvLine(line, delimiter);
              if (cells.length < 15) {
                throw new Error("Linha " + (idx + 2) + " com colunas a menos (" + cells.length + "/15).");
              }
              return {
                companyId: cells[0] || "",
                masterUrl: cells[1] || "",
                companyName: cells[2] || "",
                periodoCobranca: cells[3] || "",
                vencimentoContrato: cells[4] || "",
                mesesParaVencer: this.parseIntSafe(cells[5]),
                quantidadeNoBackoffice: this.parseIntSafe(cells[6]),
                maximoNoContrato: this.parseIntSafe(cells[7]),
                valorPorFuncionario: this.parseNum(cells[8]),
                excedentes: this.parseIntSafe(cells[9]),
                valor: this.parseNum(cells[10]),
                faturamento: cells[11] || "",
                databaseName: cells[12] || "",
                mrrAtual: this.parseNum(cells[13]),
                mrrEsperado: this.parseNum(cells[14])
              };
            });
            resolve(rows);
          } catch (e) { reject(e); }
        };
        reader.onerror = reject;
        reader.readAsText(file, "UTF-8");
      });
    },
    parseNum(raw) {
      if (raw === undefined || raw === null) return 0;
      let s = String(raw).trim();
      if (s === "") return 0;
      if (s.indexOf(",") !== -1) s = s.replace(/\./g, "").replace(",", ".");
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    },
    parseIntSafe(raw) {
      if (raw === undefined || raw === null) return 0;
      const s = String(raw).trim().replace(/[.,]/g, "");
      const n = parseInt(s, 10);
      return isNaN(n) ? 0 : n;
    },
    splitCsvLine(line, delimiter) {
      const sep = delimiter || ";";
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') { current += '"'; i++; continue; }
          inQuotes = !inQuotes;
          continue;
        }
        if (ch === sep && !inQuotes) { result.push(current); current = ""; continue; }
        current += ch;
      }
      result.push(current);
      return result;
    }
  }
};
</script>

<style lang="scss" scoped>
.recon-page {
  .page-content { padding: 24px; }
  .intro { color: #555; font-size: 13px; max-width: 900px; }

  .kpis { display: flex; gap: 14px; margin: 14px 0; flex-wrap: wrap;
    .kpi { background: #fff; border-radius: 6px; padding: 12px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
      display: flex; flex-direction: column; min-width: 120px;
      .kpi-num { font-size: 20px; font-weight: bold; color: #333; }
      .kpi-lbl { font-size: 12px; color: #888; }
      &.ok .kpi-num { color: #27ae60; }
      &.info .kpi-num { color: #2980b9; }
      &.money .kpi-num { color: #16a085; font-size: 17px; }
    }
  }

  .filters { display: flex; gap: 14px; align-items: flex-end; flex-wrap: wrap; margin-bottom: 12px;
    label { font-size: 12px; color: #555; display: flex; flex-direction: column; gap: 3px; }
    input, select { border: 1px solid #ccc; border-radius: 4px; padding: 6px 8px; }
    input[type="number"] { width: 90px; }
  }

  .import-box { background: #f7fbff; border: 1px solid #dceaf7; border-radius: 6px; padding: 14px; margin-bottom: 16px;
    font-size: 13px; color: #555;
    .import-row { margin-top: 10px; display: flex; gap: 14px; align-items: flex-end; flex-wrap: wrap;
      label { display: flex; flex-direction: column; font-size: 12px; gap: 3px; }
      input[type="date"] { border: 1px solid #ccc; border-radius: 4px; padding: 6px 8px; }
    }
  }

  .bulk-bar { background: #eef6ff; border-radius: 6px; padding: 10px 14px; margin-bottom: 10px; font-size: 13px;
    display: flex; gap: 10px; align-items: center; }

  .btn-primary { background: #3598dc; color: #fff; padding: 7px 18px; border-radius: 4px; border: none; cursor: pointer; font-weight: 600; }
  .btn-primary.danger { background: #e67e22; }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-secondary { background: #fff; color: #3598dc; border: 1px solid #3598dc; padding: 6px 16px; border-radius: 4px; cursor: pointer; }
  .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

  .recon-table { width: 100%; border-collapse: collapse; font-size: 12.5px; background: #fff;
    th, td { padding: 8px; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
    th { color: #555; font-weight: 600; background: #fafafa; }
    .shard { color: #aaa; font-size: 11px; }
    .row-applied td { background: #f4f8fb; color: #789; }
    .actions { white-space: nowrap; }
    .actions a { color: #3598dc; margin-left: 6px; }
    .applied-ok { color: #2980b9; }
    .muted { color: #bbb; }
    .btn-link { background: none; border: none; color: #3598dc; cursor: pointer; padding: 0 4px; font-size: 12px; }
    .btn-link.danger { color: #e67e22; }
    .btn-link:disabled { color: #ccc; cursor: not-allowed; }
  }

  .badge { padding: 2px 8px; border-radius: 10px; font-size: 11px; color: #fff; display: inline-block; }
  .badge-iugu { background: #ff9800; }
  .badge-asaas { background: #2980b9; }
  .badge-pago { background: #12B937; }
  .badge-pendente { background: #90a4ae; }
  .badge-vencido { background: #f4b400; }
  .badge-cancelado { background: #FF445D; }

  .empty { color: #999; text-align: center; padding: 20px 0; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex;
    align-items: center; justify-content: center; z-index: 1000; }
  .modal-card { background: #fff; border-radius: 8px; width: 560px; max-width: 94vw; max-height: 90vh; overflow: auto;
    .modal-head { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid #eee;
      h4 { margin: 0; font-weight: bold; }
      .modal-x { background: none; border: none; font-size: 22px; cursor: pointer; color: #999; }
    }
    .modal-body { padding: 18px;
      .modal-count { font-weight: 600; }
      .modal-sum { background: #f5fbff; border-radius: 4px; padding: 10px; }
      .modal-warn { background: #fff5eb; color: #c0661a; border-radius: 4px; padding: 10px; }
      .modal-hint { color: #777; font-size: 12px; }
      .form-row { margin-top: 12px;
        label { font-size: 12px; font-weight: 500; display: block; margin-bottom: 4px; }
        input { border: 1px solid #ccc; border-radius: 4px; padding: 7px 10px; width: 220px; }
      }
      .modal-result { margin-top: 14px;
        .res-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px;
          td { padding: 4px 6px; border-bottom: 1px solid #f0f0f0; }
          .res-ok td { color: #27ae60; }
          .res-err td { color: #c0392b; }
          .res-skip td { color: #999; }
        }
      }
    }
    .modal-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 18px; border-top: 1px solid #eee; }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/payment/reconciliacaoExcedentes/reconciliacaoExcedentes.vue